/**
 * Gallery registry loader.
 *
 * Strategy:
 * 1. Try fetching the live registry.json from the canonical GitHub repo
 * 2. If that fails (offline, CORS, rate-limit), fall back to the bundled
 *    /registry.json that ships with the app
 * 3. Cache the result in zustand for the session
 *
 * The bundled copy is what you see in the gallery/UI release tarball.
 * The live fetch is what you see when you're connected.
 */

import type { GalleryRegistry } from '../types/registry';

const LIVE_REGISTRY_URL =
  'https://raw.githubusercontent.com/avidtech6/fv-module-gallery/main/registry.json';

const BUNDLED_REGISTRY_URL = '/registry.json';

const FETCH_TIMEOUT_MS = 5000;

/**
 * Load the gallery registry.
 *
 * @param preferLive if true (default), try live first then bundled; if
 *   false, skip the live fetch and use bundled directly
 */
export async function loadRegistry(
  preferLive = true
): Promise<{ data: GalleryRegistry; source: 'live' | 'bundled' }> {
  if (preferLive) {
    try {
      const live = await fetchWithTimeout(LIVE_REGISTRY_URL, FETCH_TIMEOUT_MS);
      if (live.ok) {
        const data = (await live.json()) as GalleryRegistry;
        return { data, source: 'live' };
      }
    } catch (err) {
      // Live fetch failed — fall through to bundled
      console.warn('[gallery] live registry fetch failed, using bundled:', err);
    }
  }
  const bundled = await fetch(BUNDLED_REGISTRY_URL);
  if (!bundled.ok) {
    throw new Error(
      `Failed to load bundled registry: HTTP ${bundled.status} ${bundled.statusText}`
    );
  }
  const data = (await bundled.json()) as GalleryRegistry;
  return { data, source: 'bundled' };
}

async function fetchWithTimeout(url: string, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}
